import React, { useContext, useEffect, useState } from "react";
import * as XLSX from "xlsx-js-style";
import { AuthContext, LoaderContext } from "../../Context/ContextFunctions";
import { FaEdit, FaFileExport, FaFilter } from "react-icons/fa";
import NoResultFoundModel from "../../components/common/NoDataFound";
import PaginationComponent from "../../components/common/PaginationModal";
import { GetJobCategoryLookupList, GetJobList, GetJobReportList, GetJobStatusLookupList } from "../../services/Job/JobApi";
import Select from "react-select";
import { globalSelectStyles } from "../../middleware/enum";
import { GetClientLookupList } from "../../services/Client/ClientApi";
import { GetAllProjectLookupList, GetAllProjectLookupListForReports, GetProjectLookupList } from "../../services/Project/projectApi";
import { GeStatusLookupList, GetAllUserLookupList } from "../../services/Staff/StaffApi";
import FilterDialog from "../../components/common/FilterDialog";
import { Tooltip } from "react-bootstrap";

export default function JobReportListComponent() {
    //========================constant=======================
    const { userKeyID } = JSON.parse(localStorage.getItem("login"))
    const { setLoader } = useContext(LoaderContext);
    const { pageSize } = useContext(AuthContext);

    // =================== Column Configs =====================
    const tableColumnsConfig = [
        { key: "jobName", label: "Job Name" },
        { key: "projectName", label: "Project Name", },
        { key: "clientName", label: "Client Name" },
        { key: "categoryName", label: "Category" },
        { key: "assignedTeamNames", label: "Assigned Team" },
        { key: "startDate", label: "Start Date" },
        { key: "endDate", label: "End Date" },
        { key: "jobStatusName", label: "Status", },
        { key: "JobChangeStatusRemark", label: "Remark" }
    ]

    //========================state=======================
    const [valueSearch, setValueSearch] = useState(null);
    const [searchTimer, setSearchTimer] = useState(null);
    const [tableRows, setTableRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lookupList, setLookupList] = useState({
        clientList: [],
        projectList: [],
        employeeList: [],
        jobCategoryList: [],
        statusList: [],
    })
    const [appliedFilter, setAppliedFilter] = useState({
        userKeyID: userKeyID,
        pageNo: 0,
        pageSize: pageSize,
        sortingDirection: null,
        sortingColumnName: null,
        searchKeyword: null,
        fromDate: null,
        toDate: null,
    })
    //filter state
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilter, setTempFilter] = useState({
        clientID: null,
        projectID: null,
        userID: null,
        jobCategoryID: null,
        jobStatusID: null,
    });

    //========================useEffect======================= 
    useEffect(() => {
        const fetchLookupData = async () => {
            setLoader(true);
            try {
                const [
                    clientRes,
                    employeeRes,
                    categoryRes,
                    statusRes,
                ] = await Promise.all([
                    GetClientLookupList(),
                    GetAllUserLookupList(),
                    GetJobCategoryLookupList(),
                    GetJobStatusLookupList()
                ]);

                setLookupList({
                    clientList: mapToSelectOptions(
                        clientRes?.responseData?.data || [],
                        "clientName",
                        "clientID"
                    ),

                    employeeList: mapToSelectOptions(
                        employeeRes?.responseData?.data || [],
                        "name",
                        "userID"
                    ),

                    jobCategoryList: mapToSelectOptions(
                        categoryRes?.responseData?.data || [],
                        "jobCategoryName",
                        "jobCategoryID"
                    ),

                    projectList: [],   // add later if needed
                    statusList: mapToSelectOptions(
                        statusRes?.responseData?.data || [],
                        "jobStatusName",
                        "jobStatusID"
                    ),
                });
            } catch (error) {
                console.log("Lookup fetch error:", error);
            } finally {
                setLoader(false);
            }
        };

        fetchLookupData();
    }, []);



    useEffect(() => {
        async function fetchData() {
            setLoader(true);
            const response = await GetJobReportList({
                ...appliedFilter,

            });
            setLoader(false);

            if (response?.statusCode == 200) {

                setTableRows(response.responseData.data);
                setTotalRecords(response?.totalCount || response?.responseData?.data?.length || 0);
            } else {
                setTableRows([]);
                setTotalRecords(0);
            }
        }

        fetchData();
    }, [appliedFilter]);

    //========================functions=======================
    const hasActiveFilters = () => {
        const {
            clientID,
            projectID,
            userID,
            jobCategoryID,
            jobStatusID
        } = appliedFilter;

        return Boolean(
            clientID ||
            projectID ||
            userID ||
            jobCategoryID ||
            jobStatusID
        );
    };

    const mapToSelectOptions = (list = [], labelKey, valueKey) => {
        return list.map(item => ({
            label: item[labelKey],
            value: item[valueKey],
        }));
    };

    const FilterField = ({ label, children }) => (
        <div className="flex flex-col gap-1">
            <label className="text-12 font-medium text-gray-700">
                {label}
            </label>
            {children}
        </div>
    );

    const getLabelByValue = (list = [], value) => {
        return list.find(item => item.value === value)?.label || "";
    };

    const handleJobExport = () => {
        const filterRows = [];
        if (appliedFilter.clientID) {
            filterRows.push([
                "Client",
                getLabelByValue(lookupList.clientList, appliedFilter.clientID)
            ]);
        }
        if (appliedFilter.projectID) {
            filterRows.push([
                "Project",
                getLabelByValue(lookupList.projectList, appliedFilter.projectID)
            ]);
        }
        if (appliedFilter.userID) {
            filterRows.push([
                "Employee",
                getLabelByValue(lookupList.employeeList, appliedFilter.userID)
            ]);
        }
        if (appliedFilter.jobCategoryID) {
            filterRows.push([
                "Category",
                getLabelByValue(lookupList.jobCategoryList, appliedFilter.jobCategoryID)
            ]);
        }
        if (appliedFilter.jobStatusID) {
            filterRows.push([
                "Status",
                getLabelByValue(lookupList.statusList, appliedFilter.jobStatusID)
            ]);
        }
        const wb = XLSX.utils.book_new();

        const titleText = "Title: Job Report";
        const headers = [
            "Job Name",
            "Project Name",
            "Client Name",
            "Job Category",
            "Assigned Team",
            "Start Date",
            "End Date",
            "Status",
            "Remark"
        ];

        const data = tableRows.map((row) => [

            row.jobName || "-",
            row.clientName || "-",
            row.categoryName || "-",
            row.projectName || "-",
            row.assignedTeamNames || "-",
            row.startDate || "0",
            row.endDate || "0",
            `${row.jobStatusName || "-"}`,
        ]);

        const ws_data = [
            [titleText],
            [],
            ...(filterRows.length ? [["Applied Filters"]] : []),
            ...filterRows,
            ...(filterRows.length ? [[]] : []),
            [],
            headers,
            ...data
        ];
        const headerRowIndex =
            1 +                         // Title row
            1 +                         // Empty row
            (filterRows.length
                ? 1 + filterRows.length + 1 // "Applied Filters" + filters + empty
                : 0
            ) +
            1;

        const ws = XLSX.utils.aoa_to_sheet(ws_data);

        // Merge title row
        ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }];
        ws["A1"].s = {
            font: { bold: true, sz: 14 },
            alignment: { horizontal: "left", vertical: "left" },
        };

        // Header row styling (row 2 / index 2)
        headers.forEach((_, col) => {
            const cellAddr = XLSX.utils.encode_cell({ c: col, r: headerRowIndex });
            if (ws[cellAddr]) {
                ws[cellAddr].s = {
                    font: { bold: true, color: { rgb: "000000" } },
                    fill: { fgColor: { rgb: "D9E1F2" } }, // Light blue
                    alignment: { horizontal: "center", vertical: "center" },
                    border: {
                        top: { style: "medium", color: { rgb: "000000" } },
                        bottom: { style: "medium", color: { rgb: "000000" } },
                        left: { style: "medium", color: { rgb: "000000" } },
                        right: { style: "medium", color: { rgb: "000000" } },
                    },
                };
            }
        });

        const dataStartRow = headerRowIndex + 1;
        // Data row styling
        for (let r = dataStartRow; r < data.length + dataStartRow; r++) {
            for (let c = 0; c < headers.length; c++) {
                const cellAddr = XLSX.utils.encode_cell({ c, r });
                if (ws[cellAddr]) {
                    ws[cellAddr].s = {
                        font: { color: { rgb: "000000" } },
                        alignment: { horizontal: "center", vertical: "center" },
                        border: {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } },
                        },
                    };

                    // Zebra styling for alternating rows
                    if ((r - dataStartRow) % 2 === 1) {
                        ws[cellAddr].s.fill = { fgColor: { rgb: "F7F7F7" } };
                    }
                }
            }
        }

        // Column widths for readability
        ws["!cols"] = headers.map((h) => ({ wch: h.length + 8 }));

        XLSX.utils.book_append_sheet(wb, ws, "Job Report");
        XLSX.writeFile(wb, "Job_Report.xlsx");

        setAppliedFilter({
            ...appliedFilter,
            pageSize: pageSize
        })
    };

    const handleSearchFunction = (searchValue) => {
        setValueSearch(searchValue);

        // Clear old timer so API doesn't fire immediately
        if (searchTimer) clearTimeout(searchTimer);

        // Set new timer
        const timer = setTimeout(() => {
            setAppliedFilter({
                ...appliedFilter,
                searchKeyword: searchValue || null,
            });
        }, 500); // 500ms delay

        setSearchTimer(timer);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        setAppliedFilter((prev) => ({ ...prev, pageNo: pageNumber - 1 }));
    }

    //=====================render table=====================
    const renderTable = (columns = [], data = []) => {
        if (!columns.length) return null;

        return (
            <div className="flex flex-col table_wrapper border border-gray-200">
                <div className="flex-1 overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200 text-12 table-auto">
                        <thead>
                            <tr className="z-1 sticky top-0 bg-white">
                                {columns.map((col) => (
                                    <th
                                        key={col.key}
                                        className="text-12 border-b border-slate-200 bg-slate-50 whitespace-nowrap p-1"
                                    >
                                        {col.label}
                                    </th>
                                ))}

                            </tr>
                        </thead>
                        <tbody>
                            {data.length > 0 ? (
                                data.map((client, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-slate-50 border-b border-slate-200 text-center"
                                    >
                                        {columns.map((col) => {
                                            const cellValue = client[col.key] || "";
                                            const isLongText = cellValue.length > 50;

                                            return (
                                                <td
                                                    key={col.key}
                                                    className="whitespace-nowrap px-2 py-2 text-xs leading-tight"
                                                >
                                                    {isLongText ? (
                                                        <Tooltip title={cellValue} arrow>
                                                            <span className="inline-block truncate max-w-[180px] cursor-pointer">
                                                                {cellValue}
                                                            </span>
                                                        </Tooltip>
                                                    ) : (
                                                        cellValue
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    // <tr
                                    //     key={index}
                                    //     className="hover:bg-slate-50 border-b border-slate-200 text-center"
                                    // >
                                    //     {columns.map((col) => (
                                    //         <td key={col.key} className="whitespace-nowrap px-2 py-1">
                                    //             {(
                                    //                 client[col.key]
                                    //             )}
                                    //         </td>
                                    //     ))}
                                    // </tr>
                                ))
                            ) : (
                                <div className="flex justify-end">
                                    <NoResultFoundModel totalRecords={totalRecords} />
                                </div>
                            )}
                        </tbody>
                    </table>
                </div >
                {/* Pagination */}
                {(totalRecords >= pageSize) && (
                    <div className="flex justify-end">
                        <PaginationComponent
                            totalPages={Math.ceil(totalRecords / pageSize)}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}

            </div >
        );
    };

    return (
        <div>
            <>
                <div className="bg-gray-50 mx-auto rep-width">
                    <div className="h-full bg-white shadow rounded-lg flex flex-col">
                        {/* Header Section */}
                        <div className="p-2 border-b flex flex-col lg:flex-row gap-3 items-start">

                            <FilterDialog
                                maxWidth="md"
                                fullWidth
                                open={isFilterOpen}
                                title="Job Filters"
                                onClose={() => setIsFilterOpen(false)}
                                onSubmit={() => {
                                    setCurrentPage(1);
                                    setAppliedFilter(prev => ({
                                        ...prev,
                                        ...tempFilter,
                                        pageNo: 0,
                                        searchKeyword: null
                                    }));
                                    setIsFilterOpen(false);
                                }}
                            >
                                {/* Dropdown Filters */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 w-full">

                                    {/* Client filter */}

                                    <FilterField label="Client">
                                        <Select
                                            options={lookupList.clientList}
                                            placeholder="Select Client"
                                            isClearable
                                            styles={globalSelectStyles}
                                            value={lookupList.clientList.find(
                                                opt => opt.value === tempFilter.clientID
                                            ) || null}
                                            onChange={async (option) => {
                                                setTempFilter(prev => ({
                                                    ...prev,
                                                    clientID: option?.value || null,
                                                    projectID: null
                                                }));

                                                if (option?.value) {
                                                    const res = await GetAllProjectLookupListForReports(option.value);
                                                    if (res?.statusCode === 200) {
                                                        setLookupList(prev => ({
                                                            ...prev,
                                                            projectList: mapToSelectOptions(
                                                                res.responseData.data || [],
                                                                "projectName",
                                                                "projectID"
                                                            )
                                                        }));
                                                    }
                                                } else {
                                                    setLookupList(prev => ({ ...prev, projectList: [] }));
                                                }
                                            }}
                                        />
                                    </FilterField>

                                    {/* Project Name Dropdown */}
                                    <FilterField label="Project">
                                        <Select
                                            options={tempFilter.clientID ? lookupList.projectList : []}
                                            placeholder="Select Project"
                                            isClearable
                                            styles={globalSelectStyles}
                                            value={lookupList.projectList.find(
                                                opt => opt.value === tempFilter.projectID
                                            ) || null}
                                            onChange={(option) =>
                                                setTempFilter(prev => ({
                                                    ...prev,
                                                    projectID: option?.value || null
                                                }))
                                            }
                                        />
                                    </FilterField>


                                    {/* Employee Name Dropdown */}
                                    <FilterField label="Employee">
                                        <Select
                                            options={lookupList.employeeList}
                                            placeholder="Select Employee"
                                            isClearable
                                            styles={globalSelectStyles}
                                            value={lookupList.employeeList.find(
                                                opt => opt.value === tempFilter.userID
                                            ) || null}
                                            onChange={(option) =>
                                                setTempFilter(prev => ({
                                                    ...prev,
                                                    userID: option?.value || null
                                                }))
                                            }
                                        />
                                    </FilterField>


                                    {/* category starts  */}
                                    <FilterField label="Category">
                                        <Select
                                            options={lookupList.jobCategoryList}
                                            placeholder="Select Category"
                                            isClearable
                                            styles={globalSelectStyles}
                                            value={lookupList.jobCategoryList.find(
                                                opt => opt.value === tempFilter.jobCategoryID
                                            ) || null}
                                            onChange={(option) =>
                                                setTempFilter(prev => ({
                                                    ...prev,
                                                    jobCategoryID: option?.value || null
                                                }))
                                            }
                                        />
                                    </FilterField>

                                    {/* Status Name Dropdown */}
                                    <FilterField label="Status">
                                        <Select
                                            options={lookupList.statusList}
                                            placeholder="Select Status"
                                            isClearable
                                            styles={globalSelectStyles}
                                            value={lookupList.statusList.find(
                                                opt => opt.value === tempFilter.jobStatusID
                                            ) || null}
                                            onChange={(option) =>
                                                setTempFilter(prev => ({
                                                    ...prev,
                                                    jobStatusID: option?.value || null
                                                }))
                                            }
                                        />
                                    </FilterField>

                                </div>
                            </FilterDialog>

                            {/* Right-side: Export + Search */}
                            <div className="ml-auto flex items-center gap-2">

                                {/* Filter Button */}
                                <button
                                    onClick={() => {
                                        setIsFilterOpen(true);
                                        setTempFilter({ ...appliedFilter });
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2 rounded border text-12 transition-all
        ${hasActiveFilters()
                                            ? "border-[#135EA0] text-[#135EA0] bg-blue-50"
                                            : "border-gray-300 text-gray-600 hover:border-[#135EA0]"
                                        }
    `}
                                >
                                    <FaFilter
                                        size={12}
                                        className={hasActiveFilters() ? "text-[#135EA0]" : "text-gray-500"}
                                    />
                                    <span>Filter</span>
                                </button>

                                {hasActiveFilters() && (
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 px-3 py-2 rounded border border-red-300
                   text-12 text-red-600 bg-red-50
                   hover:border-red-400 hover:bg-red-100 transition-all"
                                        onClick={() => {
                                            setTempFilter({
                                                clientID: null,
                                                projectID: null,
                                                userID: null,
                                                jobCategoryID: null,
                                                jobStatusID: null,
                                            });

                                            setAppliedFilter({
                                                ...appliedFilter,
                                                clientID: null,
                                                projectID: null,
                                                userID: null,
                                                jobCategoryID: null,
                                                jobStatusID: null,
                                                pageNo: 0,
                                            });
                                        }}
                                    >
                                        Clear Filter
                                    </button>
                                )}

                                <button
                                    className="flex items-center gap-2 px-3 py-2 rounded border border-gray-300 text-12 text-gray-600 hover:border-[#135EA0] hover:text-[#135EA0]"
                                    onClick={() => {
                                        setAppliedFilter({
                                            ...appliedFilter,
                                            pageNo: 0,
                                            pageSize: 40
                                        });

                                        setTimeout(handleJobExport, 100);
                                    }}
                                >
                                    <FaFileExport size={12} />
                                    <span>Export</span>
                                </button>

                                {/* Search Input */}
                                <div className="relative w-[220px]">
                                    <input
                                        type="text"
                                        className="w-full border border-gray-200 text-12 shadow appearance-none rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline focus:ring-2 focus:ring-sky-600"
                                        placeholder="Search..."
                                        value={valueSearch}
                                        onChange={(e) => handleSearchFunction(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* filter end here  */}
                        {/* Tab Content */}
                        <div className="flex flex-col table_wrapper border border-gray-200">
                            {/* <div className="flex-1 overflow-y-auto"> */}
                            {renderTable(tableColumnsConfig, tableRows || [])}
                            {/* </div> */}
                        </div>
                    </div>
                </div>
            </>
        </div >
    )
}

